/**************************************************************************************************
//
//  Description: Display list of ASD items
//
//  Copyright:    © 2020 - 2021 Aligned Assets Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//#region Version 1.0.0.0 changes
//    001   19.10.20 Peter Bryden               Initial Revision.
//    002   15.01.21 Sean Flook         WI39041 Added an Add button and associated events.
//    003   26.01.21 Sean Flook         WI38232 Only display the add button if the user is allowed to create new records on this street.
//    004   29.01.21 Peter Bryden       WI38232 cleaned up code and handled save back to forms
//    005   02.02.21 Sean Flook         WI38232 Only allow editing of geometry if part of road selected.
//    006   19.02.21 Sean Flook         WI39185 Adjusted the width.
//    007   09.03.21 Sean Flook         WI39166 Added tooltip to icons.
//    008   09.03.21 Sean Flook         WI39240 Corrected width.
//    009   11.03.21 Sean Flook         WI39198 Changed the special designation icon.
//    010   21.03.21 Peter Bryden       WI39243 Add context to hold state of treelist open states
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion */

/* #region imports */
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Collapse from '@material-ui/core/Collapse';

import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import IconButton from "@material-ui/core/IconButton";
import { Tooltip } from "@material-ui/core";

import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import PeopleRoundedIcon from '@material-ui/icons/PeopleRounded';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import BlurOnIcon from '@material-ui/icons/BlurOn';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';

import { brown, yellow, pink, green } from "@material-ui/core/colors";
import { useAAAssociatedStreetDataListState, useAAAssociatedStreetDataUpdateState } from './AAAssociatedStreetDataListContext';

/* #endregion imports */

const icons = { PeopleRoundedIcon, ReportProblemOutlinedIcon, BlurOnIcon }

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        maxWidth: 450,
        backgroundColor: theme.palette.background.paper,
    },
    small: {
        width: theme.spacing(2),
        height: theme.spacing(2),
    },
    nested: {
        paddingLeft: theme.spacing(4),
    },
    nested_1: {
        paddingLeft: theme.spacing(10),
    },
    pink: {
        color: theme.palette.getContrastText(pink[500]),
        backgroundColor: pink[500],
    },
    brown: {
        color: theme.palette.getContrastText(brown[500]),
        backgroundColor: brown[500],
    },
    yellow: {
        color: theme.palette.getContrastText(yellow[500]),
        backgroundColor: yellow[500],
    },

}));

const AAAssociatedStreetDataListItem = ({ title, type, itemIcon, colour, data, editable, itemSelect, itemAdd }) => {
  const classes = useStyles();
  const isEditable = editable;

  const getOpen = useAAAssociatedStreetDataListState();
  const toggleOpen = useAAAssociatedStreetDataUpdateState();

  const [open,setOpen] = useState(getOpen(title))

  //FInd unique special designation types
  const handleOpenClose = () => {
    setOpen(!open);
    toggleOpen(title);
  };

  const showListIcon = (icon) => {
    switch (icon) {
      case "PeopleRoundedIcon": return <PeopleRoundedIcon className={colour && avatarColours.find((x) => x.colour === colour).classesName} />;
      case "ReportProblemOutlinedIcon": return <ReportProblemOutlinedIcon className={colour && avatarColours.find((x) => x.colour === colour).classesName} />;
      case "BlurOnIcon": return <BlurOnIcon className={colour && avatarColours.find((x) => x.colour === colour).classesName} />;
    }
  }

  const avatarColours = [{ "colour": "brown", "classesName": classes.brown },
  { "colour": "yellow", "classesName": classes.yellow },
  { "colour": "pink", "classesName": classes.pink }];

  avatarColours.find((x) => x.colour === colour);

  return (
    <React.Fragment>
      <ListItem button className="asd-ListItem" onClick={handleOpenClose}>

        {open ? <ExpandLess /> : <ExpandMore />}

        {itemIcon && itemIcon.length > 0 ?
          <ListItemIcon>
            {showListIcon(itemIcon)}
          </ListItemIcon>
          : ''}

        <ListItemText
          primary={<Typography className="form__component" variant="subtitle1"><strong>{title}</strong></Typography>}
          secondary={type ? <React.Fragment><Typography variant="subtitle2">{type}</Typography>{isEditable ? <IconButton onClick={() => itemAdd(data ? data.length : 0)} size="small"><Tooltip title="Add" arrow placement="right"><AddCircleOutlineIcon style={{ color: green[500] }} /></Tooltip></IconButton> : null}</React.Fragment> : ''}
          className={itemIcon ? '' : classes.nested} />
        <ListItemAvatar>
          <Avatar className={classes.small}>
            <Typography variant="caption">
              {`${data ? data.length : 0} `}
            </Typography>
          </Avatar>
        </ListItemAvatar>
      </ListItem>

      <Collapse in={open} timeout="auto">
        {data && data.map((d, index) => (
          <List component="div" disablePadding key={`key_${index}`}>
            <ListItem button className="asd-ListItem" className={itemIcon ? classes.nested : classes.nested_1} onClick={() => itemSelect(d.pkId, index, data.length, d.wholeRoad)}>
              <ListItemAvatar>
                <Avatar variant="square" className={`${classes.small}  ${colour && avatarColours.find((x) => x.colour === colour).classesName}`}>
                  <Typography variant="caption">
                    {`${d.id}`}
                  </Typography>
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={<Typography className="form__component" variant="body2">{`${d.displayText}`} </Typography>} />
            </ListItem>
          </List>
        ))}
      </Collapse>
    </React.Fragment>
  );
};

export default AAAssociatedStreetDataListItem;
